import { ref, computed, watch, onMounted, onUnmounted, type Ref } from "vue";
import { useTranslation } from "i18next-vue";
import { ElNotification } from "element-plus";
import { areToastsMutedOnCanvas } from "@/composable/useToast";
import { v4 as uuid } from "uuid";
import type {
  FieldObservation,
  FieldObservationType,
} from "@/types/field-observations.types";
import {
  useCreateFieldObservation,
  useUpdateFieldObservation,
  useCreateAudioRecording,
  useDeleteAudioRecording,
  useDeleteFieldObservation,
  useGetFieldParts,
  useGetFieldObservation,
} from "@/stores/queries/field-observations.query";
import { useDeleteDiagnosticImage } from "@/stores/queries/diagnostic-images.query";
import { uploadFilesBatch } from "@/utils/upload-files.utils";
import { compressImages } from "@/utils/image-compression.utils";
import { FileType } from "@/types/file.types";
import APIClient from "@/APIClient";
import { useDebounceFn } from "@vueuse/core";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { readLastSelectedFieldPart } from "@/utils/field-parts.utils";

/** Photo ou vocal déjà enregistré, avec son URL chargée à la demande. */
export interface ExistingMedia {
  id: number;
  fileId: number;
  url: string;
  isLoading: boolean;
}

export interface UseFieldObservationFormOptions {
  diagnosticId: Ref<number>;
  /** Observation existante (mode édition) — absente = création à l'ouverture. */
  observation?: FieldObservation;
  /** Photos à uploader dès la création (capture depuis la liste). */
  initialImages?: File[];
  /** Vocal à uploader dès la création (enregistrement depuis la liste). */
  initialAudioBlob?: Blob;
  /**
   * Partie imposée à la création (ex. « + » sur une partie de l'arborescence).
   * Prioritaire sur la dernière partie mémorisée.
   */
  initialPartId?: number;
  /** Appelé quand le formulaire doit se fermer (création échouée, suppression…). */
  onClose: () => void;
  /**
   * Appelé après la création de l'observation (mode création uniquement) —
   * point d'accroche pour le rattachement au plan (stamp XData).
   */
  onCreated?: (observation: FieldObservation) => void | Promise<void>;
}

/**
 * Logique partagée du formulaire d'observation terrain (création/édition,
 * sauvegarde live, photos, vocaux, suppressions). Utilisé par l'éditeur mobile
 * plein écran (`FieldObservationEditor`) et par l'éditeur compact du canvas
 * (`ObservationPanelEditor`) — un seul comportement, deux habillages.
 */
export function useFieldObservationForm(
  options: UseFieldObservationFormOptions
) {
  const { t } = useTranslation();
  const { diagnosticId } = options;

  // Current observation ID (either from options or newly created)
  const currentObservationId = ref<number | null>(
    options.observation?.id ?? null
  );
  const isInitializing = ref(!options.observation);

  // Refetch observation data to stay in sync
  const { data: observationData, refetch: refetchObservation } =
    useGetFieldObservation(computed(() => currentObservationId.value ?? 0));

  // Use observation data from query or options
  const currentObservation = computed(
    () => observationData.value ?? options.observation
  );

  // Parts data
  const { data: parts } = useGetFieldParts(diagnosticId);

  // Queries
  const { mutateAsync: createObservation } = useCreateFieldObservation();
  const { mutateAsync: updateObservation } = useUpdateFieldObservation();
  const { mutateAsync: createAudioRecording } = useCreateAudioRecording();
  const { mutateAsync: deleteAudioRecording } = useDeleteAudioRecording();
  const { mutateAsync: deleteDiagnosticImage } =
    useDeleteDiagnosticImage(diagnosticId);

  // Form state
  const selectedType = ref<FieldObservationType>(
    options.observation?.type ?? "DISORDER"
  );
  const getLastSelectedPartId = (): number | undefined => {
    if (options.observation) return options.observation.partId ?? undefined;
    if (options.initialPartId) return options.initialPartId;
    return readLastSelectedFieldPart(diagnosticId.value);
  };

  const selectedPartId = ref<number | undefined>(getLastSelectedPartId());
  /** Intitulé court (proposé par l'analyse IA à la validation d'un groupe). */
  const name = ref(options.observation?.name ?? "");
  const description = ref(options.observation?.description ?? "");

  // Clear remembered part if it no longer exists
  watch(
    parts,
    (loadedParts) => {
      if (
        !options.observation &&
        !options.initialPartId &&
        selectedPartId.value &&
        loadedParts &&
        !loadedParts.find((p) => p.id === selectedPartId.value)
      ) {
        selectedPartId.value = undefined;
      }
    },
    { once: true }
  );

  // Photos state
  const existingPhotos = ref<ExistingMedia[]>([]);
  const isUploadingPhoto = ref(false);

  // Audio recordings state
  const existingAudioRecordings = ref<ExistingMedia[]>([]);
  const isUploadingAudio = ref(false);

  // Delete media confirmation
  const showDeleteConfirm = ref(false);
  const deleteTarget = ref<{ type: "photo" | "audio"; id: number } | null>(
    null
  );
  const isDeleting = ref(false);

  // Delete observation confirmation
  const showDeleteObservationConfirm = ref(false);
  const { mutateAsync: deleteObservation, isPending: isDeletingObservation } =
    useDeleteFieldObservation();

  // Computed
  const selectedPartName = computed(() => {
    if (!selectedPartId.value || !parts.value) return "";
    const part = parts.value.find((p) => p.id === selectedPartId.value);
    return part?.name || "";
  });

  // Initialize observation on mount (create mode)
  onMounted(async () => {
    if (!options.observation) {
      // Create observation immediately. `localId` (UUID) systématique : c'est la
      // clé de rattachement plan↔observation (XData) et la clé de sync offline.
      try {
        // `partId` n'est envoyé que s'il est exploitable : un NaN/null y serait
        // sérialisé en `null` et refusé par la validation du back.
        const partId = Number(selectedPartId.value);
        const newObs = await createObservation({
          type: selectedType.value,
          diagnosticId: diagnosticId.value,
          ...(Number.isFinite(partId) && partId > 0 ? { partId } : {}),
          localId: uuid(),
        });
        currentObservationId.value = newObs.id;

        await options.onCreated?.(newObs);

        // Handle initial images if provided (from camera capture in list)
        if (options.initialImages && options.initialImages.length > 0) {
          await uploadPhotos(options.initialImages);
        }

        // Handle initial audio blob if provided
        if (options.initialAudioBlob) {
          await uploadAudioBlob(options.initialAudioBlob);
        }
      } catch (error) {
        console.error("Failed to create observation:", error);
        options.onClose();
        return;
      } finally {
        isInitializing.value = false;
      }
    } else {
      // Edit mode - load existing data
      loadExistingPhotos();
      loadExistingAudioRecordings();
    }
  });

  // Load existing photos
  const loadExistingPhotos = async () => {
    const photos = currentObservation.value?.photos;
    if (!photos || photos.length === 0) {
      existingPhotos.value = [];
      return;
    }

    existingPhotos.value = photos.map((photo) => ({
      id: photo.id,
      fileId: photo.fileId,
      url: "",
      isLoading: true,
    }));

    await Promise.all(
      existingPhotos.value.map(async (photo, index) => {
        try {
          const { url } = await APIClient.getFileUrl(photo.fileId);
          existingPhotos.value[index].url = url;
        } catch (e) {
          console.error("Failed to load photo URL:", e);
        } finally {
          existingPhotos.value[index].isLoading = false;
        }
      })
    );
  };

  // Load existing audio recordings
  const loadExistingAudioRecordings = async () => {
    const audioRecordings = currentObservation.value?.audioRecordings;
    if (!audioRecordings || audioRecordings.length === 0) {
      existingAudioRecordings.value = [];
      return;
    }

    existingAudioRecordings.value = audioRecordings.map((audio) => ({
      id: audio.id,
      fileId: audio.fileId,
      url: "",
      isLoading: true,
    }));

    await Promise.all(
      existingAudioRecordings.value.map(async (audio, index) => {
        try {
          const { url } = await APIClient.getFileUrl(audio.fileId);
          existingAudioRecordings.value[index].url = url;
        } catch (e) {
          console.error("Failed to load audio URL:", e);
        } finally {
          existingAudioRecordings.value[index].isLoading = false;
        }
      })
    );
  };

  // Watch for observation data changes and reload
  watch(
    () => observationData.value,
    () => {
      if (observationData.value) {
        loadExistingPhotos();
        loadExistingAudioRecordings();
      }
    }
  );

  // Live save: Type change
  watch(selectedType, async (newType) => {
    if (!currentObservationId.value || isInitializing.value) return;
    try {
      await updateObservation({
        id: currentObservationId.value,
        payload: { type: newType },
      });
    } catch (error) {
      console.error("Failed to update type:", error);
    }
  });

  // Persist last selected part per diagnostic
  const saveLastSelectedPart = (partId: number | undefined) => {
    try {
      const stored = $ls.getItem(LocalStorageKey.LAST_SELECTED_FIELD_PART);
      const map = stored ? JSON.parse(stored) : {};
      if (partId) {
        map[diagnosticId.value] = partId;
      } else {
        delete map[diagnosticId.value];
      }
      $ls.setItem(
        LocalStorageKey.LAST_SELECTED_FIELD_PART,
        JSON.stringify(map)
      );
    } catch {
      // Ignore storage errors
    }
  };

  // Live save: Part change
  watch(selectedPartId, async (newPartId) => {
    saveLastSelectedPart(newPartId);
    if (!currentObservationId.value || isInitializing.value) return;
    try {
      await updateObservation({
        id: currentObservationId.value,
        payload: { partId: newPartId ?? null },
      });
    } catch (error) {
      console.error("Failed to update part:", error);
    }
  });

  // Live save: Description change (debounced)
  const saveDescription = useDebounceFn(async () => {
    if (!currentObservationId.value) return;
    try {
      await updateObservation({
        id: currentObservationId.value,
        payload: { description: description.value || undefined },
      });
    } catch (error) {
      console.error("Failed to update description:", error);
    }
  }, 500);

  watch(description, () => {
    if (!isInitializing.value) {
      saveDescription();
    }
  });

  // Live save: Name change (debounced). Un nom vidé est enregistré vide : le
  // titre de l'observation retombe alors sur son type.
  const saveName = useDebounceFn(async () => {
    if (!currentObservationId.value) return;
    try {
      await updateObservation({
        id: currentObservationId.value,
        payload: { name: name.value.trim() },
      });
    } catch (error) {
      console.error("Failed to update name:", error);
    }
  }, 500);

  watch(name, () => {
    if (!isInitializing.value) {
      saveName();
    }
  });

  /** @returns les ids des DiagnosticImages créées (pour post-traitement, ex. n° de marqueur). */
  const uploadPhotos = async (files: File[]): Promise<number[]> => {
    if (!currentObservationId.value || files.length === 0) return [];

    isUploadingPhoto.value = true;
    try {
      const compressedFiles = await compressImages(files, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
        maxSizeBytes: 1024 * 1024,
      });

      const items = compressedFiles.map((file) => ({
        file,
        fileType: FileType.DIAGNOSTIC_IMAGE,
        params: { diagnosticId: diagnosticId.value },
      }));

      const results = await uploadFilesBatch(items);
      const newPhotoIds = results
        .filter((r) => r.diagnosticImageId !== undefined)
        .map((r) => r.diagnosticImageId as number);

      if (newPhotoIds.length > 0) {
        // Get current photo IDs and add new ones
        const currentPhotoIds = existingPhotos.value.map((p) => p.id);
        await updateObservation({
          id: currentObservationId.value,
          payload: { photoIds: [...currentPhotoIds, ...newPhotoIds] },
        });

        // Refresh observation data
        await refetchObservation();
      }
      return newPhotoIds;
    } catch (error) {
      console.error("Failed to upload photos:", error);
      return [];
    } finally {
      isUploadingPhoto.value = false;
    }
  };

  const uploadAudioBlob = async (audioBlob: Blob) => {
    if (!currentObservationId.value) return;

    isUploadingAudio.value = true;
    try {
      const extension = audioBlob.type.includes("webm") ? "webm" : "mp4";
      const audioFile = new File(
        [audioBlob],
        `voice_${Date.now()}.${extension}`,
        { type: audioBlob.type }
      );

      const uploadResults = await uploadFilesBatch([
        {
          file: audioFile,
          fileType: FileType.OBSERVATION_AUDIO,
          params: { diagnosticId: diagnosticId.value },
        },
      ]);

      if (uploadResults.length && uploadResults[0].fileId) {
        await createAudioRecording({
          fileId: uploadResults[0].fileId,
          observationId: currentObservationId.value,
        });

        // Refresh observation data
        await refetchObservation();
      }
    } catch (error) {
      console.error("Failed to upload audio:", error);
    } finally {
      isUploadingAudio.value = false;
    }
  };

  // Delete handling
  const confirmDeletePhoto = (photoId: number) => {
    deleteTarget.value = { type: "photo", id: photoId };
    showDeleteConfirm.value = true;
  };

  const confirmDeleteAudio = (audioId: number) => {
    deleteTarget.value = { type: "audio", id: audioId };
    showDeleteConfirm.value = true;
  };

  const handleDelete = async () => {
    if (!deleteTarget.value || !currentObservationId.value) return;

    isDeleting.value = true;
    try {
      if (deleteTarget.value.type === "photo") {
        await deleteDiagnosticImage(deleteTarget.value.id);
        existingPhotos.value = existingPhotos.value.filter(
          (p) => p.id !== deleteTarget.value!.id
        );
        // Aucun toast dans l'outil dessin (cf. useToast)
        if (!areToastsMutedOnCanvas.value) {
          ElNotification({
            message: t("dashboard:fieldObservations.photos.deleted"),
            type: "success",
            duration: 2000,
            position: "bottom-right",
          });
        }
      } else {
        await deleteAudioRecording({
          id: deleteTarget.value.id,
          observationId: currentObservationId.value,
        });
        existingAudioRecordings.value = existingAudioRecordings.value.filter(
          (a) => a.id !== deleteTarget.value!.id
        );
        // Aucun toast dans l'outil dessin (cf. useToast)
        if (!areToastsMutedOnCanvas.value) {
          ElNotification({
            message: t("dashboard:fieldObservations.voice.deleted"),
            type: "success",
            duration: 2000,
            position: "bottom-right",
          });
        }
      }
      // La liste locale ne suffit pas : les vues qui raisonnent sur les photos
      // du serveur (association photo ↔ n° de marqueur) verraient encore le
      // média supprimé, et le numéro resterait « couvert » jusqu'au prochain
      // rechargement de la page.
      await refetchObservation();
    } catch (error) {
      console.error("Failed to delete:", error);
    } finally {
      isDeleting.value = false;
      showDeleteConfirm.value = false;
      deleteTarget.value = null;
    }
  };

  // Delete observation handler
  const handleDeleteObservation = async () => {
    if (!currentObservationId.value) return;

    try {
      await deleteObservation({
        id: currentObservationId.value,
        diagnosticId: diagnosticId.value,
      });
      showDeleteObservationConfirm.value = false;
      options.onClose();
    } catch (error) {
      console.error("Failed to delete observation:", error);
    }
  };

  onUnmounted(() => {
    // Cleanup any blob URLs
    existingPhotos.value = [];
    existingAudioRecordings.value = [];
  });

  return {
    // state
    currentObservationId,
    currentObservation,
    isInitializing,
    selectedType,
    selectedPartId,
    selectedPartName,
    name,
    description,
    parts,
    existingPhotos,
    existingAudioRecordings,
    isUploadingPhoto,
    isUploadingAudio,
    showDeleteConfirm,
    isDeleting,
    showDeleteObservationConfirm,
    isDeletingObservation,
    // actions
    refetchObservation,
    uploadPhotos,
    uploadAudioBlob,
    confirmDeletePhoto,
    confirmDeleteAudio,
    handleDelete,
    handleDeleteObservation,
  };
}
